import _ from "lodash";
import { CSSProperties, PureComponent, ReactNode } from "react";

export type IconLibrary = "material" | "fontawesome";
export type MaterialIconType = "filled" | "outlined" | "round" | "sharp" | "two-tone";
export type FontAwesomeIconType = "solid" | "regular" | "brands";
export type IconType = MaterialIconType | FontAwesomeIconType;

const MATERIAL_ICON_CLASS_MAP: Record<MaterialIconType, string> = {
  filled: "material-icons",
  outlined: "material-icons-outlined",
  round: "material-icons-round",
  sharp: "material-icons-sharp",
  "two-tone": "material-icons-two-tone"
};

const FA_PREFIX_MAP: Record<FontAwesomeIconType, string> = {
  solid: "fa-solid",
  regular: "fa-regular",
  brands: "fa-brands"
};

export const MATERIAL_ICON_TYPES: readonly MaterialIconType[] = ["filled", "outlined", "round", "sharp", "two-tone"];
export const FA_ICON_TYPES: readonly FontAwesomeIconType[] = ["solid", "regular", "brands"];

export const isMaterialIconType = (value: string | undefined): value is MaterialIconType =>
  value !== undefined && (MATERIAL_ICON_TYPES as readonly string[]).includes(value);

export const isFontAwesomeIconType = (value: string | undefined): value is FontAwesomeIconType =>
  value !== undefined && (FA_ICON_TYPES as readonly string[]).includes(value);

interface IKIconProps {
  iconName: string;
  style?: CSSProperties;
  showCursorPointer?: boolean;
  onClick?: (e: any) => void;
  className?: string;
  iconType?: IconType;
  iconLibrary?: IconLibrary;
}

interface IKIconState {}

class KIcon extends PureComponent<IKIconProps, IKIconState> {
  constructor(props: IKIconProps) {
    super(props);
    this.state = {};
  }

  getProps = () => {
    let result: any = {};
    if (this.props && this.props.style) {
      result = _.clone(this.props);
      if (result && result.style) {
        delete result.style;
      }
      if (result && result.iconType) {
        delete result.iconType;
      }
      if (result && result.iconLibrary) {
        delete result.iconLibrary;
      }
    }
    return result;
  };

  getStyleProp = () => {
    let style: any = {};

    if (this.props.style) {
      style = _.cloneDeep(this.props.style);
      if (this.props.style?.fontFamily) {
        delete style.fontFamily;
      }
    }
    if (this.props.showCursorPointer) {
      style.cursor = "pointer";
    }
    return style;
  };

  isFontAwesome = (): boolean => {
    return this.props.iconLibrary === "fontawesome";
  };

  getMaterialClassName = (): string => {
    if (isMaterialIconType(this.props.iconType)) {
      return MATERIAL_ICON_CLASS_MAP[this.props.iconType];
    }
    return "material-icons";
  };

  getFontAwesomeClassName = (): string => {
    const faType: FontAwesomeIconType = isFontAwesomeIconType(this.props.iconType) ? this.props.iconType : "solid";
    const prefix = FA_PREFIX_MAP[faType];
    const name = this.props.iconName ? `fa-${this.props.iconName}` : "";
    return `${prefix} ${name}`.trim();
  };

  render(): ReactNode {
    const isFa: boolean = this.isFontAwesome();
    if (isFa && !this.props.iconName) {
      return null;
    }
    const baseClass: string = isFa ? this.getFontAwesomeClassName() : this.getMaterialClassName();
    const fullClass: string = `${baseClass} md-48${this.props.className ? ` ${this.props.className}` : ""}`;
    const innerContent: string | null = isFa ? null : this.props.iconName;

    return (
      <>
        <i
          onClick={(e: any) => this.props.onClick && this.props.onClick(e)}
          style={this.getStyleProp()}
          {...this.getProps()}
          className={fullClass}
        >
          {innerContent}
        </i>
      </>
    );
  }
}

const kIcon = KIcon;
export { kIcon as KIcon };
